// @ts-check
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { emptyArray } from 'ui-components/default-props/empty';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import { ServiceMappingRow } from './row';
import ModalPortal from '../../../../../components/modal-portal';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { FillMethods } from '../../../../../enums/fill-methods';
import { LogService } from '../../../../../services/log-service';
import { updateSellerSettingsBulk } from '../../../api/save-settings';
import Global from '../../../../../store/global-selectors';
import * as SellerActions from '../../../../../store/seller/seller.actions';
import {
  deleteMappingForStore,
  fetchMappingsForStore,
  mapRequestedService,
} from '../../../../../store/map-requested-service/map-requested-service.api';
import { useServiceMappingsSetupCompleteSellerSetting } from '../../../../../hooks/use-seller-setting';
import sharedMessages from '../messages';
import EditModal from '../shipping-services-modal';
import style from '../style.module.less';

/**
 * @typedef {import('./service-mapping').ServiceMapping} ServiceMapping
 * @typedef {import('./service-mapping').ShapedService} ShapedService
 */

/**
 * @param {object} props
 * @param {string} props.storeGuid
 * @returns {JSX.Element}
 */
export const ShippingServicesTab = ({ storeGuid }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [editMapping, setEditMapping] = useState(/** @type {ShapedService | null} */ (null));
  const [serviceMappings, setServiceMappings] = useState(/** @type {ServiceMapping[]} */ (emptyArray));
  const packages = useSelector(Global.selectSellerPackages);
  const fulfillmentProviders = useSelector(Global.selectSellerActiveFulfillmentProviders);
  const fulfillmentServices = useSelector(Global.selectSellerFulfillmentServices);
  const isServiceMappingsSetup = useServiceMappingsSetupCompleteSellerSetting();

  const loadServiceMappings = useCallback(() => {
    setIsLoading(true);
    fetchMappingsForStore(storeGuid)
      .then((mappings) => {
        setServiceMappings(mappings);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [setIsLoading, storeGuid]);

  useEffect(() => {
    loadServiceMappings();
  }, [loadServiceMappings]);

  const addServiceMapping = useCallback(() => {
    /** @type {ShapedService} */
    const newMapping = {
      fillMethod: FillMethods.shipping,
      requestedServiceCode: '',
      serviceId: '',
    };
    setEditMapping(newMapping);
  }, []);

  const deleteServiceMapping = useCallback(
    /**
     * @param {ShapedService} shapedServiceMapping
     */
    (shapedServiceMapping) => {
      deleteMappingForStore({ ...shapedServiceMapping, storeGuid })
        .then(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.serviceMappingDeletedTitle} />,
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: <FormattedMessage {...messages.serviceMappingDeletedContent} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
          loadServiceMappings();
        })
        .catch(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.serviceMappingErrorTitle} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.serviceMappingErrorContent} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
        });
    },
    [loadServiceMappings, storeGuid],
  );

  const closeModal = () => {
    setEditMapping(null);
  };

  const onFormSave = useCallback(
    /**
     * @param {ServiceMapping} serviceMapping
     */
    (serviceMapping) => {
      mapRequestedService({ ...serviceMapping, storeGuid })
        .then(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.serviceMappingSuccessTitle} />,
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: <FormattedMessage {...messages.serviceMappingSuccessContent} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
          loadServiceMappings();
          if (!isServiceMappingsSetup) {
            updateSellerSettingsBulk({ [SellerSettings.isServiceMappingsSetupComplete.key]: 'true' })
              .then((newSettings) => dispatch(SellerActions.saveNewSellerSettings(newSettings)))
              .catch((e) => LogService.logError(new Error('Failed to save service mappings setup setting', { cause: e })));
          }
        })
        .catch(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.serviceMappingErrorTitle} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.serviceMappingErrorContent} />,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
        });
      closeModal();
    },
    [dispatch, isServiceMappingsSetup, loadServiceMappings, storeGuid],
  );

  const renderGridHeader = () => (
    <>
      <div className={style['service-header-cell']}>
        <FormattedMessage {...sharedMessages.requestedService} />
      </div>
      <div className={style['service-header-cell']}>
        <FormattedMessage {...sharedMessages.shippingService} />
      </div>
      <div className={style['service-header-cell']}>
        <FormattedMessage {...sharedMessages.fulfillment} />
      </div>
      <div className={style['service-header-cell-right']}>
        <FormattedMessage {...sharedMessages.actions} />
      </div>
    </>
  );

  return (
    <>
      {editMapping ? (
        <ModalPortal onClose={closeModal}>
          <EditModal
            fulfillmentProviders={fulfillmentProviders}
            fulfillmentServices={fulfillmentServices}
            newServiceMapping={!editMapping.index}
            onFormSave={onFormSave}
            sellerPackages={packages}
            serviceMapping={editMapping}
          />
        </ModalPortal>
      ) : null}

      <div className={style['tab-content']}>
        <div className={style['tab-header']}>
          <FormattedMessage {...sharedMessages.shippingServiceDescription} />
        </div>
        <div className={style['shipping-services-body']}>
          <Button className={style['add-service-mapping-button']} onClick={addServiceMapping} theme='primary'>
            <FormattedMessage {...sharedMessages.addServiceMapping} />
          </Button>
          {isLoading ? (
            <LoadingIndicator className={style['table-loading-indicator']} />
          ) : (
            <div className={style['service-mapping-grid']}>
              {renderGridHeader()}
              {serviceMappings.map((service, index) => (
                <ServiceMappingRow
                  key={`${service.requestedServiceCode}-${index}`}
                  deleteServiceMapping={deleteServiceMapping}
                  index={index}
                  service={service}
                  setEditMapping={setEditMapping}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
